import { Target, Telescope, Handshake, Lightbulb, Wrench, BadgeCheck } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { Reveal, Eyebrow } from "@/components/Reveal";

const STORY_IMG =
    "https://images.pexels.com/photos/1181336/pexels-photo-1181336.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const VALUES = [
    { icon: Handshake, title: "Transparency", body: "Open roadmaps, honest timelines and no black boxes, you always know where your program stands." },
    { icon: Lightbulb, title: "Relentless Curiosity", body: "Our AI Center of Excellence prototypes what's next so our clients are never catching up." },
    { icon: Wrench, title: "Engineering Craft", body: "Standardized frameworks and senior engineers on every engagement, quality is a system, not a hope." },
    { icon: BadgeCheck, title: "Ownership", body: "One accountable team from strategy to production and beyond, with SLAs we actually stand behind." },
];

export default function AboutPage() {
    return (
        <PageShell
            eyebrow="About Syntera"
            title="Engineering the autonomous enterprise,"
            accent="from Bengaluru to the world."
            sub="Syntera is an AI-first technology services company. We help enterprises move faster, work smarter and grow stronger through intelligent, secure and scalable engineering."
            testid="about-page"
        >
            <section className="py-20 sm:py-28" data-testid="about-story">
                <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
                    <Reveal>
                        <div className="relative">
                            <div className="absolute -inset-4 rounded-3xl bg-volt/5 blur-2xl" />
                            <div className="relative overflow-hidden rounded-2xl border border-white/10">
                                <img
                                    src={STORY_IMG}
                                    alt="Syntera engineering leadership collaborating"
                                    className="h-[420px] w-full object-cover transition-transform duration-700 hover:scale-105"
                                    data-testid="about-story-image"
                                />
                                <div className="absolute inset-0 bg-gradient-to-tr from-[#07090E]/70 via-transparent to-transparent" />
                            </div>
                        </div>
                    </Reveal>
                    <div>
                        <Reveal>
                            <Eyebrow>Our Story</Eyebrow>
                            <h2 className="mb-5 font-syne text-2xl font-bold tracking-tight text-white sm:text-3xl" data-testid="about-story-heading">
                                Built in India's technology capital, trusted across borders
                            </h2>
                            <p className="text-base leading-relaxed text-slate-400">
                                Syntera was founded in Bengaluru with a simple conviction: enterprises
                                deserve AI and cloud solutions that are as reliable as they are ambitious.
                                Today our teams deliver programs across India, APAC and beyond, spanning
                                generative AI, hybrid cloud, zero-trust cybersecurity and big data
                                engineering.
                            </p>
                            <p className="mt-4 text-base leading-relaxed text-slate-400">
                                We nurture a culture of transparency, curiosity and continuous learning,
                                and it shows in software we ship: 847 detection patterns, 28 programming
                                languages covered and 68 automated tests passing since our founding in 2026.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="border-y border-white/[0.06] bg-panel/40 py-20 sm:py-24">
                <div className="mx-auto grid max-w-7xl gap-4 px-5 sm:px-8 lg:grid-cols-2">
                    {[
                        { icon: Target, title: "Our Mission", body: "To simplify the complex, turning AI, cloud and security from boardroom buzzwords into production systems that compound business value.", testid: "about-mission-card" },
                        { icon: Telescope, title: "Our Vision", body: "Autonomous enterprise ecosystems: businesses whose operations learn, adapt and improve continuously, powered by trustworthy AI built responsibly.", testid: "about-vision-card" },
                    ].map((c, i) => (
                        <Reveal key={c.title} delay={i * 0.12}>
                            <div data-testid={c.testid} className="card-glow-border h-full rounded-2xl border border-white/[0.08] bg-card2/50 p-9">
                                <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-xl border border-volt/20 bg-volt/10 text-volt">
                                    <c.icon size={22} />
                                </div>
                                <h3 className="mb-3 font-syne text-xl font-semibold text-white sm:text-2xl">{c.title}</h3>
                                <p className="text-sm leading-relaxed text-slate-400 sm:text-base">{c.body}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="py-20 sm:py-28">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal>
                        <Eyebrow>How We Work</Eyebrow>
                        <h2 className="mb-4 font-syne text-2xl font-bold tracking-tight text-white sm:text-3xl" data-testid="about-values-heading">
                            Values that ship
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {VALUES.map((v, i) => (
                            <Reveal key={v.title} delay={i * 0.08}>
                                <div data-testid={`about-value-card-${i}`} className="card-glow-border h-full rounded-2xl border border-white/[0.08] bg-card2/50 p-7">
                                    <div className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-volt/20 bg-volt/10 text-volt">
                                        <v.icon size={20} />
                                    </div>
                                    <h3 className="mb-2 font-syne text-lg font-semibold text-white">{v.title}</h3>
                                    <p className="text-sm leading-relaxed text-slate-400">{v.body}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </PageShell>
    );
}
